// Routines — templated runs of steps that recur on a schedule or fire on triggers, ring with their own look,
// run in their own mode and are measured against goals. Pure. docs/plans/routines.md.
//
// A routine is a `processes/{id}` doc (the old Processes, rebuilt in place — same collection). A run is a
// `processExecutions/{runId}` doc with a DETERMINISTIC id (`<routineId>_<date>_<n>`) so the phone and the web
// both creating today's run land on one doc. Its steps are real tasks with ids `<runId>_<stepId>`.
// Missed runs are computed (a scheduled occurrence whose window passed with no run), never written.
import { localDateStr } from './time.js';
import { makeTaskDoc } from './taskdoc.js';
import { addDaysStr, metricScore, scoreZone } from './phonestats.js';

export const ROUTINE_LAYOUTS = [
  { key: 'cards', label: 'Cards', hint: 'One step per card — swipe done or skip' },
  { key: 'focus', label: 'Focus', hint: 'One step big, with a countdown of its minutes' },
  { key: 'checklist', label: 'Checklist', hint: 'Every step on one screen, tick in any order' },
  { key: 'guided', label: 'Guided', hint: 'Timed steps that move on by themselves, read out loud' },
];

export const ROUTINE_TRIGGERS = [
  { key: 'schedule', label: 'On a schedule', icon: 'calendar' },
  { key: 'clock_in', label: 'When I clock in', icon: 'play' },
  { key: 'clock_out', label: 'When I clock out', icon: 'square' },
  { key: 'arrive', label: 'When I arrive at a job', icon: 'map-pin' },
  { key: 'leave', label: 'When I leave a job', icon: 'log-out' },
  { key: 'first_unlock', label: 'First phone unlock of the day', icon: 'smartphone' },
  { key: 'after', label: 'After another routine', icon: 'repeat' },
  { key: 'task_done', label: 'When a task is done', icon: 'check-circle' },
  { key: 'manual', label: 'Only when I start it', icon: 'hand' },
];

export const CAPTURE_TYPES = [
  { key: 'check', label: 'Just tick it' },
  { key: 'number', label: 'A number' },
  { key: 'yesno', label: 'Yes / no' },
  { key: 'rating', label: 'Rating 1–5' },
  { key: 'text', label: 'A line of text' },
  { key: 'photo', label: 'A photo' },
];

export const RING_STYLES = ['banner', 'fullscreen', 'alarm'];
export const RUN_OPEN = new Set(['due', 'running']);
export const SKIP_REASONS = ['Not needed today', 'No time', 'Sick', 'Away', 'Did it another way'];

const DAY_MS = 86400000;
const pad = (n) => String(n).padStart(2, '0');

/** 'HH:MM' → minutes after midnight (null when not a time). */
export function hmToMin(s) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(s || '').trim());
  if (!m) return null;
  const h = Number(m[1]); const mi = Number(m[2]);
  return h < 24 && mi < 60 ? h * 60 + mi : null;
}
export const minToHm = (m) => (Number.isFinite(m) ? `${pad(Math.floor(m / 60) % 24)}:${pad(Math.round(m) % 60)}` : null);
/** Minutes after local midnight of a ms instant. */
export const msToMin = (ms) => { if (!Number.isFinite(ms)) return null; const d = new Date(ms); return d.getHours() * 60 + d.getMinutes() + d.getSeconds() / 60; };
/** A local date + 'HH:MM' → ms (DST-safe: built from the local wall clock). */
export const atLocal = (date, hm) => new Date(`${date}T${hm || '00:00'}:00`).getTime();

// ---- the routine doc ---------------------------------------------------------------------------------

function legacyTriggers(t) {
  if (!t || !t.type || t.type === 'manual') return [{ type: 'manual' }];
  if (t.type === 'event') {
    const projectId = t.eventFilter?.projectId || null;
    if (t.event === 'task_complete') return [{ type: 'task_done', projectId }];
    return [{ type: t.event, projectId }];
  }
  if (t.type === 'time') {
    const time = t.time || '09:00';
    if (t.schedule === 'weekly') return [{ type: 'schedule', freq: 'weekly', days: [Number(t.dayOfWeek) || 0], time }];
    if (t.schedule === 'monthly') return [{ type: 'schedule', freq: 'monthly', dayOfMonth: Number(t.dayOfMonth) || 1, time }];
    return [{ type: 'schedule', freq: 'daily', time }];
  }
  return [{ type: 'manual' }];
}

function normStep(s, i) {
  return {
    stepId: s.stepId || `s${i + 1}`,
    title: String(s.title || '').trim(),
    minutes: Number(s.minutes ?? s.estimatedMinutes) > 0 ? Number(s.minutes ?? s.estimatedMinutes) : null,
    note: s.note ?? s.description ?? '',
    optional: !!s.optional,
    capture: s.capture && s.capture.type && s.capture.type !== 'check' ? { type: s.capture.type, unit: s.capture.unit || null, label: s.capture.label || null } : null,
    photo: s.photo || null,
    link: s.link && s.link.type ? s.link : null,
  };
}

/** Any routine / old process doc → the full routine shape (defaults filled, old single `trigger` read). */
export function normalizeRoutine(p) {
  if (!p) return null;
  const name = String(p.name || '').trim() || 'Routine';
  const triggers = Array.isArray(p.triggers) && p.triggers.length ? p.triggers.filter((t) => t && t.type) : legacyTriggers(p.trigger);
  const look = p.look || {};
  const ring = p.ring || {};
  return {
    ...p,
    name,
    enabled: p.enabled !== false,
    scope: p.scope === 'team' ? 'team' : 'me',
    ownerUid: p.ownerUid || null,
    projectId: p.projectId || null,
    // fixed = always its project · ask = you pick the project each time you start it (projectId = the one pre-picked)
    projectMode: p.projectMode === 'ask' ? 'ask' : 'fixed',
    departmentId: p.departmentId || null,
    maxPerDay: Math.max(1, Number(p.maxPerDay) || 1),
    steps: (p.steps || []).slice().sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)).map(normStep).filter((s) => s.title),
    triggers,
    ring: {
      style: RING_STYLES.includes(ring.style) ? ring.style : 'fullscreen',
      leadMin: Math.max(0, Number(ring.leadMin) || 0),
      snooze: Array.isArray(ring.snooze) && ring.snooze.length ? ring.snooze : null,
      speak: ring.speak || null,
    },
    look: {
      color: look.color || p.color || '#FB5446',
      icon: look.icon || p.icon || '🔁',
      kicker: look.kicker || `${name.toUpperCase()}`,
      message: look.message || null,
      layout: ROUTINE_LAYOUTS.some((l) => l.key === look.layout) ? look.layout : 'cards',
      showSteps: look.showSteps !== false,
      sound: ['default', 'alarm', 'none'].includes(look.sound) ? look.sound : 'default',
      autoAdvance: !!look.autoAdvance,
    },
    goals: Array.isArray(p.goals) ? p.goals.filter((g) => g && g.metric) : [],
    automations: Array.isArray(p.automations) ? p.automations : [],
    pausedUntil: p.pausedUntil || null,
  };
}

/** Can `uid` see / run this routine? Personal routines belong to their owner; team ones to everyone. */
export const routineVisible = (r, uid) => !!r && (r.scope === 'team' || !r.ownerUid || !uid || r.ownerUid === uid);
const live = (r, date) => r.enabled && !(r.pausedUntil && date && date <= r.pausedUntil);

/** Planned minutes for the whole routine (steps without minutes count 0). */
export const plannedMinutes = (r) => (r?.steps || []).reduce((s, x) => s + (Number(x.minutes) || 0), 0);

// ---- schedules -------------------------------------------------------------------------------------------

const dayDiff = (a, b) => Math.round((new Date(`${b}T12:00:00`) - new Date(`${a}T12:00:00`)) / DAY_MS);

/** Does a schedule trigger fall on `date`? */
export function scheduleMatches(t, date) {
  if (!t || t.type !== 'schedule') return false;
  if (t.from && date < t.from) return false;
  if (t.until && date > t.until) return false;
  const d = new Date(`${date}T12:00:00`);
  const dow = d.getDay();
  const days = Array.isArray(t.days) ? t.days.map(Number) : [];
  switch (t.freq || 'daily') {
    case 'daily': return !days.length || days.includes(dow);
    case 'weekly': return (days.length ? days : [Number(t.dayOfWeek) || 0]).includes(dow);
    case 'interval': {
      const every = Math.max(1, Number(t.every) || 1);
      const diff = dayDiff(t.from || date, date);
      return diff >= 0 && diff % every === 0;
    }
    case 'monthly': {
      const dom = d.getDate();
      const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
      if (t.nth) {   // { n: 1..4 | -1 (last), weekday: 0..6 }
        if (dow !== Number(t.nth.weekday)) return false;
        const n = Number(t.nth.n);
        return n === -1 ? dom + 7 > last : Math.ceil(dom / 7) === n;
      }
      const want = Math.min(Number(t.dayOfMonth) || 1, last);
      return dom === want;
    }
    default: return false;
  }
}

/**
 * Scheduled occurrences of one routine between two dates (inclusive):
 * [{ routineId, date, at, until, triggerIndex }] — `until` = the latest start (the window), default end of day.
 */
export function scheduleOccurrences(routine, from, to) {
  const r = routine?.triggers ? routine : normalizeRoutine(routine);
  if (!r) return [];
  const out = [];
  for (let date = from, n = 0; date <= to && n < 400; date = addDaysStr(date, 1), n++) {
    if (!live(r, date)) continue;
    r.triggers.forEach((t, i) => {
      if (!scheduleMatches(t, date)) return;
      const at = atLocal(date, t.time || '09:00');
      const win = Number(t.window) > 0 ? Number(t.window) * 60000 : atLocal(addDaysStr(date, 1), '00:00') - at - 1;
      out.push({ routineId: r.id, date, at, until: at + win, triggerIndex: i });
    });
  }
  // one per date (the earliest) unless the routine runs more than once a day
  const seen = new Map();
  for (const o of out.sort((a, b) => a.at - b.at)) {
    const k = o.date; const c = seen.get(k) || 0;
    if (c < r.maxPerDay) { seen.set(k, c + 1); o.n = c + 1; }
    else o.drop = true;
  }
  return out.filter((o) => !o.drop);
}

/** Does an event fire this routine? event = { type, at, projectId?, routineId?, taskId? } → the trigger or null. */
export function matchesEvent(routine, event) {
  const r = routine?.triggers ? routine : normalizeRoutine(routine);
  if (!r || !event?.type || !live(r, localDateStr(event.at || Date.now()))) return null;
  for (const t of r.triggers) {
    if (t.type !== event.type) continue;
    if (['clock_in', 'clock_out', 'arrive', 'leave', 'task_done'].includes(t.type)) {
      if (t.projectId && t.projectId !== event.projectId) continue;
      if (t.type === 'task_done' && event.projectId && event.projectId === r.projectId && event.routineRun) continue;   // its own steps never re-fire it
      return t;
    }
    if (t.type === 'after') {
      if (t.routineId && t.routineId === event.routineId) return t;
      continue;
    }
    if (t.type === 'first_unlock') {
      const m = msToMin(event.at);
      const lo = hmToMin(t.after) ?? 0; const hi = hmToMin(t.before) ?? 24 * 60;
      if (m >= lo && m < hi) return t;
    }
  }
  return null;
}

// ---- runs ------------------------------------------------------------------------------------------------

export const runIdFor = (routineId, date, n = 1) => `${routineId}_${date}_${n}`;
export const stepTaskId = (runId, stepId) => `${runId}_${stepId}`;

/** Runs of a routine on a date, for uid (personal runs belong to whoever ran them). */
export const runsOn = (runs, routineId, date, uid = null) => (runs || []).filter((x) => x.routineId === routineId && x.date === date && (!uid || !x.uid || x.uid === uid));

/** The next free slot for a routine on a date (1..maxPerDay), or null when it's full. */
export function nextSlot(routine, runs, date, uid = null) {
  const taken = new Set(runsOn(runs, routine.id, date, uid).map((x) => x.n || 1));
  for (let n = 1; n <= (routine.maxPerDay || 1); n++) if (!taken.has(n)) return n;
  return null;
}

/** A fresh run doc (not started). */
export function buildRun(routine, { date, n = 1, trigger = { type: 'manual' }, dueAt = null, uid = null, now = Date.now(), projectId }) {
  const r = routine?.triggers ? routine : normalizeRoutine(routine);
  const id = runIdFor(r.id, date, n);
  return {
    id, uid, routineId: r.id, routineName: r.name, date, n,
    trigger: { type: trigger.type || 'manual', at: trigger.at || now, ...(trigger.projectId ? { projectId: trigger.projectId } : {}) },
    dueAt: Number.isFinite(dueAt) ? dueAt : now,
    status: 'due', startedAt: null, finishedAt: null, pausedAt: null, skipReason: null,
    // A routine that asks: the run carries the project picked for it (the routine's own is only the pre-pick).
    projectId: (r.projectMode === 'ask' && projectId !== undefined ? projectId : r.projectId) || null,
    steps: r.steps.map((s) => ({ stepId: s.stepId, taskId: stepTaskId(id, s.stepId), title: s.title, minutes: s.minutes, optional: s.optional, capture: s.capture ? s.capture.type : null, startedAt: null, doneAt: null, skipped: false, value: null })),
    metrics: null, goalResults: null, clockBefore: null,
    // the old Processes fields, so older readers still list it
    processId: r.id, processName: r.name, scheduledDate: date, executedAt: now, triggerType: trigger.type || 'manual',
    createdAt: now, updatedAt: now,
  };
}

/** The project a run's steps and time go under: the run's own (picked at start when the routine asks), else the routine's. */
export const runProject = (r, run) => (run && 'projectId' in run ? run.projectId : r?.projectId) || null;

/** The run's step tasks (deterministic ids), in step order, on the run's project and date. */
export function runTasks(routine, run, now = Date.now()) {
  const r = routine?.triggers ? routine : normalizeRoutine(routine);
  const byStep = new Map(r.steps.map((s) => [s.stepId, s]));
  return run.steps.map((st, i) => {
    const s = byStep.get(st.stepId) || st;
    return makeTaskDoc({
      id: st.taskId, title: s.title, description: s.note || '', projectId: runProject(r, run), scheduledDate: run.date,
      estimatedMinutes: s.minutes || null, sortOrder: (i + 1) * 1000, processId: r.id, processStepId: st.stepId, runId: run.id,
      ...(s.photo ? { photo: s.photo } : {}),
    }, [], now + i);
  });
}

/** The step the run is on: the first not done / skipped (in order), or null when all are through. */
export const currentStep = (run) => (run?.steps || []).find((s) => !s.doneAt && !s.skipped) || null;

/** { done, skipped, total, required, requiredDone, pct } */
export function runProgress(run) {
  const steps = run?.steps || [];
  const done = steps.filter((s) => s.doneAt).length;
  const skipped = steps.filter((s) => s.skipped).length;
  const req = steps.filter((s) => !s.optional);
  const requiredDone = req.filter((s) => s.doneAt).length;
  // handled = done or skipped on purpose; a run where every required step was handled (and something was done) is
  // a finished run — "partial" is one that stopped with steps never reached.
  const requiredHandled = req.filter((s) => s.doneAt || s.skipped).length;
  return { done, skipped, total: steps.length, required: req.length, requiredDone, requiredHandled, pct: steps.length ? done / steps.length : 0 };
}

/** Finished-run status from its steps: done when every required step was done or skipped (and one was done), else partial. */
export function runOutcome(run) {
  const p = runProgress(run);
  if (p.required > 0) return p.requiredHandled === p.required && p.done > 0 ? 'done' : 'partial';
  return p.done > 0 ? 'done' : 'partial';
}

/** The run's state for a person looking at it now: upcoming | due | running | paused | done | partial | skipped | missed. */
export function runState(run, { now = Date.now(), until = null } = {}) {
  if (!run) return null;
  if (run.status === 'running') return run.pausedAt ? 'paused' : 'running';
  if (run.status === 'due') {
    if (Number.isFinite(until) && now > until) return 'missed';
    if (run.date < localDateStr(now)) return 'missed';
    return run.dueAt > now ? 'upcoming' : 'due';
  }
  return run.status;
}

/** The run in progress for uid (the routine mode is on while it isn't paused). */
export const activeRun = (runs, uid = null) => (runs || []).filter((x) => x.status === 'running' && (!uid || !x.uid || x.uid === uid)).sort((a, b) => (b.startedAt || 0) - (a.startedAt || 0))[0] || null;

// ---- metrics ---------------------------------------------------------------------------------------------

const median = (xs) => {
  const a = xs.filter((x) => x != null && Number.isFinite(x)).sort((p, q) => p - q);
  if (!a.length) return null;
  const m = Math.floor(a.length / 2);
  return a.length % 2 ? a[m] : (a[m - 1] + a[m]) / 2;
};
const round1 = (x) => (x == null ? null : Math.round(x * 10) / 10);

/**
 * One run's numbers. extra = { firstUnlockAt?, distractMsBefore? } (from the phone's day, when there is one).
 * Times are minutes after midnight; durations minutes.
 */
export function runMetrics(run, extra = {}) {
  if (!run) return null;
  const p = runProgress(run);
  const steps = (run.steps || []).map((s) => {
    const min = s.doneAt && s.startedAt ? (s.doneAt - s.startedAt) / 60000 : null;
    return { stepId: s.stepId, min: round1(min), planned: s.minutes || null, onPace: min != null && s.minutes ? min <= s.minutes : null, skipped: !!s.skipped, value: s.value ?? null };
  });
  const paced = steps.filter((s) => s.onPace != null);
  const values = {};
  for (const s of run.steps || []) if (s.value != null && s.value !== '') values[s.stepId] = s.value;
  return {
    started: !!run.startedAt,
    completed: run.status === 'done' || (run.status !== 'due' && run.status !== 'running' && runOutcome(run) === 'done'),
    completion: round1(p.pct * 100),
    startMin: round1(msToMin(run.startedAt)),
    finishMin: run.finishedAt ? round1(msToMin(run.finishedAt)) : null,
    durationMin: run.startedAt && run.finishedAt ? round1((run.finishedAt - run.startedAt) / 60000) : null,
    latencyMin: run.startedAt && Number.isFinite(run.dueAt) ? round1(Math.max(0, run.startedAt - run.dueAt) / 60000) : null,
    stepsOnPace: paced.length ? round1((paced.filter((s) => s.onPace).length / paced.length) * 100) : null,
    steps,
    values,
    wakeMin: Number.isFinite(extra.firstUnlockAt) ? round1(msToMin(extra.firstUnlockAt)) : null,
    distractMin: Number.isFinite(extra.distractMsBefore) ? round1(extra.distractMsBefore / 60000) : null,
    skipped: run.status === 'skipped',
  };
}

// ---- goals -----------------------------------------------------------------------------------------------

/**
 * Every kind of goal a routine can hold. `per: 'run'` goals are judged on each run; `per: 'period'` goals on a
 * stretch of days (stats). `value` is minutes-after-midnight for times (entered as 'HH:MM'), minutes for
 * durations, a percentage for rates, a count for counts.
 */
export const GOAL_METRICS = [
  { key: 'startBy', label: 'Start by', kind: 'time', per: 'run', dir: 'down' },
  { key: 'finishBy', label: 'Finish by', kind: 'time', per: 'run', dir: 'down' },
  { key: 'startWithin', label: 'Start within … of the ring', kind: 'minutes', per: 'run', dir: 'down' },
  { key: 'durationUnder', label: 'Take no longer than', kind: 'minutes', per: 'run', dir: 'down' },
  { key: 'durationOver', label: 'Spend at least', kind: 'minutes', per: 'run', dir: 'up' },
  { key: 'completion', label: 'Complete at least', kind: 'percent', per: 'run', dir: 'up' },
  { key: 'stepsOnPace', label: 'Steps within their minutes', kind: 'percent', per: 'run', dir: 'up' },
  { key: 'value', label: 'A step’s number', kind: 'value', per: 'run', dir: 'up' },
  { key: 'wakeBy', label: 'Awake (first unlock) by', kind: 'time', per: 'run', dir: 'down' },
  { key: 'noDistract', label: 'Distracting apps before it’s done, at most', kind: 'minutes', per: 'run', dir: 'down' },
  { key: 'consistency', label: 'Start within ± of my usual time', kind: 'minutes', per: 'run', dir: 'down' },
  { key: 'timesPer', label: 'Times per week / month', kind: 'count', per: 'period', dir: 'up' },
  { key: 'everyScheduled', label: 'Every scheduled day', kind: 'none', per: 'period', dir: 'up' },
  { key: 'xOfY', label: 'X of the last Y days', kind: 'xy', per: 'period', dir: 'up' },
  { key: 'streak', label: 'Keep a streak of', kind: 'count', per: 'period', dir: 'up' },
];
const GOAL_BY = new Map(GOAL_METRICS.map((g) => [g.key, g]));
export const goalMeta = (key) => GOAL_BY.get(key) || null;

const fmtMin = (m) => (m == null ? '—' : m > 0 && m < 1 ? '<1 min' : m >= 60 ? `${Math.floor(m / 60)} h ${Math.round(m % 60)} min` : `${Math.round(m)} min`);
const fmtClock = (m) => {
  if (m == null) return '—';
  const h = Math.floor(m / 60) % 24; const mi = Math.round(m % 60);
  return `${h % 12 || 12}:${pad(mi)} ${h < 12 ? 'AM' : 'PM'}`;
};

/** A goal as a sentence: "Finish by 5:00 AM", "4 times a week", "Push-ups at least 30". */
export function goalLabel(g, routine = null) {
  if (!g) return '';
  if (g.label) return g.label;
  const v = g.value;
  switch (g.metric) {
    case 'startBy': return `Start by ${fmtClock(v)}`;
    case 'finishBy': return `Finish by ${fmtClock(v)}`;
    case 'wakeBy': return `Awake by ${fmtClock(v)}`;
    case 'startWithin': return `Start within ${fmtMin(v)} of the ring`;
    case 'durationUnder': return `Done in ${fmtMin(v)} or less`;
    case 'durationOver': return `At least ${fmtMin(v)}`;
    case 'completion': return `${v}% of steps done`;
    case 'stepsOnPace': return `${v}% of steps within their minutes`;
    case 'noDistract': return `≤ ${fmtMin(v)} on distracting apps before it's done`;
    case 'consistency': return `Start within ±${fmtMin(v)} of my usual time`;
    case 'timesPer': return `${v} times a ${g.period === 'month' ? 'month' : 'week'}`;
    case 'everyScheduled': return 'Every scheduled day';
    case 'xOfY': return `${g.x} of the last ${g.y} days`;
    case 'streak': return `A ${v}-day streak`;
    case 'value': {
      const step = routine?.steps?.find((s) => s.stepId === g.stepId);
      const name = step?.capture?.label || step?.title || 'Value';
      const unit = step?.capture?.unit ? ` ${step.capture.unit}` : '';
      if (g.op === 'between') return `${name} ${v}–${g.max}${unit}`;
      return `${name} ${g.op === 'lte' ? 'at most' : 'at least'} ${v}${unit}`;
    }
    default: return goalMeta(g.metric)?.label || g.metric;
  }
}

/**
 * One run-goal on one run → { met: true | false | null (can't tell), actual, target }.
 * history = earlier runs' metrics (for consistency).
 */
export function evaluateRunGoal(g, metrics, history = []) {
  if (!g || !metrics) return { met: null, actual: null, target: g?.value ?? null };
  const v = Number(g.value);
  const le = (a) => (a == null ? null : a <= v);
  const ge = (a) => (a == null ? null : a >= v);
  let actual = null; let met = null;
  switch (g.metric) {
    case 'startBy': actual = metrics.startMin; met = le(actual); break;
    case 'finishBy': actual = metrics.finishMin; met = metrics.completed ? le(actual) : (metrics.started ? false : null); break;
    case 'wakeBy': actual = metrics.wakeMin; met = le(actual); break;
    case 'startWithin': actual = metrics.latencyMin; met = le(actual); break;
    case 'durationUnder': actual = metrics.durationMin; met = le(actual); break;
    case 'durationOver': actual = metrics.durationMin; met = ge(actual); break;
    case 'completion': actual = metrics.completion; met = metrics.started ? ge(actual) : null; break;
    case 'stepsOnPace': actual = metrics.stepsOnPace; met = ge(actual); break;
    case 'noDistract': actual = metrics.distractMin; met = le(actual); break;
    case 'consistency': {
      const usual = median(history.map((h) => h?.startMin));
      if (usual == null || metrics.startMin == null) break;
      actual = round1(Math.abs(metrics.startMin - usual)); met = actual <= v; break;
    }
    case 'value': {
      const raw = metrics.values?.[g.stepId];
      actual = raw == null || raw === '' ? null : Number(raw);
      if (actual == null || !Number.isFinite(actual)) { actual = null; break; }
      met = g.op === 'lte' ? actual <= v : g.op === 'between' ? actual >= v && actual <= Number(g.max) : actual >= v;
      break;
    }
    default: return { met: null, actual: null, target: g.value ?? null };
  }
  return { met, actual, target: g.value ?? null };
}

/** Every run-goal of a routine on one run → [{ id, metric, label, met, actual, target }]. */
export function evaluateRunGoals(routine, metrics, history = []) {
  return (routine?.goals || []).filter((g) => (goalMeta(g.metric)?.per || 'run') === 'run').map((g, i) => ({
    id: g.id || `g${i + 1}`, metric: g.metric, label: goalLabel(g, routine), ...evaluateRunGoal(g, metrics, history),
  }));
}

// ---- stats -----------------------------------------------------------------------------------------------

const weekStart = (date) => { const d = new Date(`${date}T12:00:00`); return addDaysStr(date, -d.getDay()); };   // Sunday (the pay week)
const monthStart = (date) => `${date.slice(0, 8)}01`;

/** Days a run was finished (done or partial with every required step) — the "did it" days. */
const didIt = (x) => x && (x.status === 'done' || (x.status === 'partial' && x.metrics?.completed));

/**
 * Everything the analytics show for one routine between `from` and `to` (dates), judged as of `now`.
 *   days      [{ date, expected, state, run, metrics, goalsMet }]  (state: done | partial | skipped | missed | due | upcoming | running | none)
 *   hitRate   % of expected days done (days still to come don't count)
 *   goalRate  % of done runs with every run-goal met
 *   streak    { current, best } over expected days (a skip with a reason is neutral, a miss breaks it)
 *   strength  0–100 habit strength (Loop-style: exponentially weighted, one miss doesn't zero it)
 *   score     0–100 (hit rate 50, goals 30, strength 20) + zone
 *   typical   { startMin, finishMin, durationMin } medians;  trend [{ date, startMin, finishMin, durationMin }]
 *   weekdays  [{ dow, expected, done }];  steps [{ stepId, title, avgMin, planned, onPaceRate, skipRate }]
 *   skipReasons [{ reason, n }];  periodGoals [{ id, label, met, actual, target }]
 */
export function routineStats(routine, runs = [], { from, to, now = Date.now(), uid = null, extraByDate = {} } = {}) {
  const r = routine?.triggers ? routine : normalizeRoutine(routine);
  const today = localDateStr(now);
  // Days before the routine existed aren't misses (its first run counts even if it's older, e.g. imported).
  const born = Number.isFinite(r.createdAt) && r.createdAt > 0 ? localDateStr(r.createdAt) : null;
  const firstRun = (runs || []).filter((x) => x.routineId === r.id).reduce((a, x) => (!a || x.date < a ? x.date : a), null);
  const start = [born, firstRun].filter(Boolean).sort()[0] || from;
  if (start > from) from = start > to ? to : start;
  const mine = (runs || []).filter((x) => x.routineId === r.id && (!uid || !x.uid || x.uid === uid));
  const byDate = new Map();
  // one run per day: the most complete one (a second run after a partial, or a stale due n=1, mustn't hide it)
  const RANK = { done: 5, partial: 4, running: 3, due: 2, skipped: 1 };
  const rank = (x) => (RANK[x.status === 'partial' && runOutcome(x) === 'done' ? 'done' : x.status] || 0);
  for (const x of mine) { const had = byDate.get(x.date); if (!had || rank(x) > rank(had) || (rank(x) === rank(had) && (x.n || 1) < (had.n || 1))) byDate.set(x.date, x); }
  // an occurrence before the routine was made (earlier the day it was made) isn't one
  const occ = new Map(scheduleOccurrences(r, from, to).filter((o) => !(Number.isFinite(r.createdAt) && r.createdAt > 0 && o.until < r.createdAt)).map((o) => [o.date, o]));
  const scheduled = r.triggers.some((t) => t.type === 'schedule');

  const days = [];
  const doneMetrics = [];
  for (let date = from, n = 0; date <= to && n < 800; date = addDaysStr(date, 1), n++) {
    const run = byDate.get(date) || null;
    const o = occ.get(date) || null;
    const expected = !!o || !!run;
    let state = 'none';
    if (run) state = runState(run, { now, until: o?.until ?? null });
    else if (o) state = date > today || (date === today && now <= o.until) ? (now < o.at ? 'upcoming' : 'due') : 'missed';
    if (state === 'partial' && (run?.metrics?.completed || runOutcome(run) === 'done')) state = 'done';   // runs saved before skips counted
    const metrics = run ? (run.metrics || runMetrics(run, extraByDate[date] || {})) : null;
    let goalsMet = null;
    if (metrics && (state === 'done' || state === 'partial')) {
      const res = evaluateRunGoals(r, metrics, doneMetrics.slice(-14));
      const judged = res.filter((g) => g.met != null);
      goalsMet = judged.length ? judged.every((g) => g.met) : null;
      if (state === 'done') doneMetrics.push(metrics);
    }
    days.push({ date, expected, state, run, metrics, goalsMet });
  }

  const judged = days.filter((d) => d.expected && !['upcoming', 'due', 'running', 'paused', 'none'].includes(d.state));
  const counted = judged.filter((d) => d.state !== 'skipped');
  const done = counted.filter((d) => d.state === 'done');
  const hitRate = counted.length ? round1((done.length / counted.length) * 100) : null;
  const goalDays = done.filter((d) => d.goalsMet != null);
  const goalRate = goalDays.length ? round1((goalDays.filter((d) => d.goalsMet).length / goalDays.length) * 100) : null;

  // streaks over expected days (skips neutral; today still open doesn't break it)
  let best = 0; let streakRun = 0;
  for (const d of days) {
    if (!d.expected || d.state === 'skipped' || d.state === 'upcoming' || d.state === 'due' || d.state === 'running' || d.state === 'paused') continue;
    if (d.state === 'done') { streakRun += 1; best = Math.max(best, streakRun); } else streakRun = 0;
  }
  const cur = streakRun;

  // habit strength: per expected day, s = s·m + hit·(1−m); m from how often it's expected (Loop Habit Tracker)
  const expectedDays = days.filter((d) => d.expected).length;
  const freq = days.length ? Math.max(1 / 30, expectedDays / days.length) : 1;
  const m = Math.pow(0.5, Math.sqrt(freq) / 13);
  let s = 0;
  for (const d of judged) {
    if (d.state === 'skipped') continue;
    s = s * m + (d.state === 'done' ? 1 : d.state === 'partial' ? 0.5 : 0) * (1 - m);
  }
  const strength = judged.length ? Math.round(s * 100) : null;

  const parts = [[hitRate, 50], [goalRate, 30], [strength, 20]].filter(([v]) => v != null);
  const w = parts.reduce((a, [, x]) => a + x, 0);
  const score = w ? Math.round(parts.reduce((a, [v, x]) => a + v * x, 0) / w) : null;

  const trend = done.map((d) => ({ date: d.date, startMin: d.metrics?.startMin ?? null, finishMin: d.metrics?.finishMin ?? null, durationMin: d.metrics?.durationMin ?? null }));
  const weekdays = Array.from({ length: 7 }, (_, dow) => ({ dow, expected: 0, done: 0 }));
  for (const d of counted) { const dow = new Date(`${d.date}T12:00:00`).getDay(); weekdays[dow].expected += 1; if (d.state === 'done') weekdays[dow].done += 1; }

  const stepStats = r.steps.map((st) => {
    const rows = days.flatMap((d) => (d.metrics?.steps || []).filter((x) => x.stepId === st.stepId));
    const timed = rows.filter((x) => x.min != null);
    const paced = rows.filter((x) => x.onPace != null);
    return {
      stepId: st.stepId, title: st.title, planned: st.minutes || null,
      avgMin: round1(median(timed.map((x) => x.min))),
      onPaceRate: paced.length ? round1((paced.filter((x) => x.onPace).length / paced.length) * 100) : null,
      skipRate: rows.length ? round1((rows.filter((x) => x.skipped).length / rows.length) * 100) : null,
    };
  });

  const reasons = new Map();
  for (const d of days) if (d.state === 'skipped') { const k = d.run?.skipReason || 'No reason'; reasons.set(k, (reasons.get(k) || 0) + 1); }

  const periodGoals = (r.goals || []).filter((g) => goalMeta(g.metric)?.per === 'period').map((g, i) => {
    let met = null; let actual = null; let target = g.value ?? null;
    if (g.metric === 'timesPer') {
      const start = g.period === 'month' ? monthStart(to) : weekStart(to);
      actual = days.filter((d) => d.date >= start && d.state === 'done').length; met = actual >= Number(g.value);
    } else if (g.metric === 'everyScheduled') {
      const start = weekStart(to);
      const wk = days.filter((d) => d.date >= start && d.expected && !['upcoming', 'due', 'running', 'paused', 'skipped'].includes(d.state));
      actual = wk.filter((d) => d.state === 'done').length; target = wk.length; met = wk.length ? actual === wk.length : null;
    } else if (g.metric === 'xOfY') {
      const y = Math.max(1, Number(g.y) || 7);
      const start = addDaysStr(to, -(y - 1));
      actual = days.filter((d) => d.date >= start && d.state === 'done').length; target = Number(g.x) || 0; met = actual >= target;
    } else if (g.metric === 'streak') {
      actual = cur; met = cur >= Number(g.value);
    }
    return { id: g.id || `p${i + 1}`, metric: g.metric, label: goalLabel(g, r), met, actual, target };
  });

  return {
    routineId: r.id, from, to, scheduled, days, expected: counted.length, done: done.length,
    hitRate, goalRate, streak: { current: cur, best }, strength, score, zone: scoreZone(score),
    typical: {
      startMin: round1(median(done.map((d) => d.metrics?.startMin))),
      finishMin: round1(median(done.map((d) => d.metrics?.finishMin))),
      durationMin: round1(median(done.map((d) => d.metrics?.durationMin))),
    },
    trend, weekdays, steps: stepStats,
    skipReasons: [...reasons.entries()].map(([reason, n]) => ({ reason, n })).sort((a, b) => b.n - a.n),
    periodGoals,
  };
}

/** A goal's score (0–100) with direction — up goals score higher above target (metricScore is "lower is better"). */
export function directedScore(value, goal, dir = 'down') {
  if (value == null || !(goal > 0)) return null;
  return dir === 'up' ? metricScore(goal, Math.max(value, 1e-9)) : metricScore(value, goal);
}

// ---- today ----------------------------------------------------------------------------------------------

/**
 * Today's routines for uid, soonest first: [{ routine, run, state, at, until }]. Scheduled ones show from the
 * start of the day (upcoming → due → running / done…); event / manual ones only once they have a run.
 */
export function todayRoutines(routines = [], runs = [], { now = Date.now(), uid = null } = {}) {
  const date = localDateStr(now);
  const out = [];
  for (const raw of routines) {
    const r = normalizeRoutine(raw);
    if (!r || !routineVisible(r, uid)) continue;
    const occ = r.enabled ? scheduleOccurrences(r, date, date) : [];
    const rs = runsOn(runs, r.id, date, uid);
    const slots = Math.max(occ.length, rs.length);
    for (let i = 0; i < slots; i++) {
      const o = occ[i] || null;
      const run = rs.find((x) => (x.n || 1) === (o?.n || i + 1)) || rs[i] || null;
      const state = run ? runState(run, { now, until: o?.until ?? null }) : (now < o.at ? 'upcoming' : now <= o.until ? 'due' : 'missed');
      out.push({ routine: r, run, state, at: run?.dueAt ?? o?.at ?? null, until: o?.until ?? null });
    }
  }
  return out.sort((a, b) => (a.at ?? Infinity) - (b.at ?? Infinity));
}

/**
 * Scheduled occurrences that are due now and have no run yet — the app materializes them on open so today's
 * plan shows them (idempotent: the run id is deterministic).
 */
export function dueToMaterialize(routines = [], runs = [], { now = Date.now(), uid = null } = {}) {
  const date = localDateStr(now);
  const out = [];
  for (const raw of routines) {
    const r = normalizeRoutine(raw);
    if (!r || !routineVisible(r, uid)) continue;
    for (const o of scheduleOccurrences(r, date, date)) {
      if (o.at - r.ring.leadMin * 60000 > now || now > o.until) continue;
      if (runsOn(runs, r.id, date, uid).some((x) => (x.n || 1) === o.n)) continue;
      out.push({ routineId: r.id, date, n: o.n, dueAt: o.at, trigger: { type: 'schedule', at: o.at } });
    }
  }
  return out;
}

/**
 * The rings routines make between two dates: virtual reminders for the reminder pipeline (deriveAttached).
 * A run already started / done / skipped for that slot silences it.
 */
export function routineRings(routines = [], runs = [], { from, to, uid = null } = {}) {
  const out = [];
  for (const raw of routines) {
    const r = normalizeRoutine(raw);
    if (!r || !routineVisible(r, uid)) continue;
    for (const o of scheduleOccurrences(r, from, to)) {
      const run = runsOn(runs, r.id, o.date, uid).find((x) => (x.n || 1) === o.n);
      if (run && run.status !== 'due') continue;
      const steps = r.steps.map((s) => s.title);
      const mins = plannedMinutes(r);
      out.push({
        id: `rt:${r.id}:${o.date}:${o.n}`, virtual: true, title: r.name,
        body: r.look.message || (steps.length ? `${steps.length} step${steps.length === 1 ? '' : 's'}${mins ? ` · about ${fmtMin(mins)}` : ''}` : null),
        at: o.at - r.ring.leadMin * 60000, style: r.ring.style, link: { type: 'routine', id: r.id },
        routineId: r.id, date: o.date, n: o.n, snooze: r.ring.snooze || undefined, speak: r.ring.speak || null,
        look: { color: r.look.color, icon: r.look.icon, kicker: r.look.kicker, sound: r.look.sound, steps: r.look.showSteps ? steps.slice(0, 8) : [] },
        status: 'scheduled', source: 'routine',
      });
    }
    // Runs an event made (clock out, leaving a job, first unlock…) ring when they were made — not the quiet ones
    // ("after another routine": the finish screen offers it; manual: you're already starting it).
    for (const run of (runs || []).filter((x) => x.routineId === r.id && x.status === 'due' && x.date >= from && x.date <= to && (!uid || !x.uid || x.uid === uid))) {
      const kind = run.trigger?.type;
      if (!kind || kind === 'schedule' || kind === 'after' || kind === 'manual') continue;
      out.push({
        id: `rt:${r.id}:${run.date}:${run.n || 1}`, virtual: true, title: r.name,
        body: r.look.message || `${r.steps.length} step${r.steps.length === 1 ? '' : 's'}`,
        at: run.dueAt, style: r.ring.style, link: { type: 'routine', id: r.id },
        routineId: r.id, date: run.date, n: run.n || 1, snooze: r.ring.snooze || undefined, speak: r.ring.speak || null,
        look: { color: r.look.color, icon: r.look.icon, kicker: r.look.kicker, sound: r.look.sound, steps: r.look.showSteps ? r.steps.map((x) => x.title).slice(0, 8) : [] },
        status: 'scheduled', source: 'routine', event: kind,
      });
    }
  }
  return out;
}

/** `rt:<routineId>:<date>:<n>` → { routineId, date, n } (a routine ring's virtual id). */
export function routineOfVirtual(v) {
  const m = /^rt:(.+):(\d{4}-\d{2}-\d{2}):(\d+)$/.exec(String(v || ''));
  return m ? { routineId: m[1], date: m[2], n: Number(m[3]) } : null;
}

export { fmtMin as formatRoutineMinutes, fmtClock as formatClockMin };

// ---- editing -------------------------------------------------------------------------------------------

const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const ordinal = (n) => (n === -1 ? 'last' : ['first', 'second', 'third', 'fourth'][n - 1] || `${n}th`);

/** One trigger as words: "Weekdays at 4:45 AM", "When I leave Joy build", "First unlock, 3:00–10:00 AM". */
export function triggerLabel(t, { projects = [], routines = [] } = {}) {
  if (!t) return '';
  const pName = (id) => projects.find((p) => p.id === id)?.name || 'a job';
  const time = t.time ? ` at ${fmtClock(hmToMin(t.time))}` : '';
  switch (t.type) {
    case 'schedule': {
      const days = Array.isArray(t.days) ? [...t.days].map(Number).sort() : [];
      const dayWords = days.length === 7 || !days.length ? 'Every day'
        : days.join() === '1,2,3,4,5' ? 'Weekdays' : days.join() === '0,6' ? 'Weekends' : days.map((d) => DAY_SHORT[d]).join(' ');
      if (t.freq === 'weekly') return `${days.length ? days.map((d) => DAY_SHORT[d]).join(' ') : DAY_SHORT[Number(t.dayOfWeek) || 0]} every week${time}`;
      if (t.freq === 'interval') return `Every ${Math.max(1, Number(t.every) || 1)} days${time}`;
      if (t.freq === 'monthly') return t.nth ? `The ${ordinal(Number(t.nth.n))} ${DAY_SHORT[Number(t.nth.weekday) || 0]} of the month${time}` : `Day ${t.dayOfMonth || 1} of the month${time}`;
      return `${dayWords}${time}`;
    }
    case 'clock_in': return t.projectId ? `When I clock in to ${pName(t.projectId)}` : 'When I clock in';
    case 'clock_out': return t.projectId ? `When I clock out of ${pName(t.projectId)}` : 'When I clock out';
    case 'arrive': return `When I arrive at ${t.projectId ? pName(t.projectId) : 'a job'}`;
    case 'leave': return `When I leave ${t.projectId ? pName(t.projectId) : 'a job'}`;
    case 'task_done': return t.projectId ? `When a task in ${pName(t.projectId)} is done` : 'When a task is done';
    case 'first_unlock': return `First phone unlock, ${fmtClock(hmToMin(t.after) ?? 0)}–${fmtClock(hmToMin(t.before) ?? 24 * 60)}`;
    case 'after': return `After ${routines.find((x) => x.id === t.routineId)?.name || 'another routine'}`;
    case 'manual': return 'Only when I start it';
    default: return t.type;
  }
}

/** The routine's triggers as one line (the list shows it). */
export const routineWhenLabel = (r, lookups = {}) => (r?.triggers || []).map((t) => triggerLabel(t, lookups)).join(' · ');

/** A blank routine for the editors. */
export function blankRoutine(uid = null) {
  return normalizeRoutine({
    id: null, name: '', ownerUid: uid, scope: 'me', enabled: true,
    steps: [{ stepId: 's1', title: '', minutes: 5 }],
    triggers: [{ type: 'schedule', freq: 'daily', time: '07:00' }],
    look: { color: '#FB5446', icon: '🌅', layout: 'cards' }, ring: { style: 'fullscreen' }, goals: [],
  });
}

/**
 * The routine as it's saved: trimmed, steps with ids and order, the kicker defaulted from the name, and its project
 * with that project's department (required unless it asks for the project at each start — throws without one).
 * → { doc, project: null } (`project` stays for callers that wrote a made project first; none is made now).
 */
export function prepareRoutine(input, { uid = null, projects = [], departments = [], now = Date.now(), newId }) {
  const name = String(input?.name || '').trim();
  if (!name) throw new Error('name required');
  const steps = (input.steps || []).filter((s) => String(s.title || '').trim()).map((s, i) => ({
    ...normStep(s, i), stepId: s.stepId || `s${now.toString(36)}${i}`, sortOrder: i,
  }));
  if (!steps.length) throw new Error('a routine needs at least one step');
  const r = normalizeRoutine({ ...input, name, steps, look: { ...(input.look || {}), kicker: input.look?.kicker?.trim() || name.toUpperCase() } });
  // Every routine belongs to a project (Lj: routines go under the project they're for — no shared "Routines" bucket),
  // or asks which project each time it starts (projectMode 'ask'; its project, if any, is the pre-pick).
  // Its department is the project's.
  const project = r.projectId ? projects.find((p) => p.id === r.projectId) : null;
  if (r.projectId && !project) throw new Error('that project is gone — pick another');
  if (!project && r.projectMode !== 'ask') throw new Error('pick the project this routine is for, or have it ask when you start it');
  const projectId = project?.id || null;
  const departmentId = project ? project.departmentId || null : r.departmentId || null;
  const { id, ...rest } = r;
  const doc = {
    ...rest, id: id || newId(), projectId, departmentId, ownerUid: r.ownerUid || uid,
    // the old Processes fields some readers still use
    icon: r.look.icon, color: r.look.color, trigger: null,
    createdAt: input.createdAt || now, updatedAt: now,
  };
  return { doc, project: null };
}

/**
 * A day's routines for the day report (dayreport.js): [{ routineId, name, icon, color, state, startMin, finishMin,
 * durationMin, goalsMet, skipReason }] — the scheduled ones and any that ran, in the order they were due.
 */
export function routineDay(routines = [], runs = [], date, { now = Date.now(), uid = null } = {}) {
  const out = [];
  for (const raw of routines) {
    const r = normalizeRoutine(raw);
    if (!r || !routineVisible(r, uid)) continue;
    const d = routineStats(r, runs, { from: date, to: date, now, uid }).days.find((x) => x.date === date);
    if (!d || !d.expected) continue;
    out.push({
      routineId: r.id, name: r.name, icon: r.look.icon, color: r.look.color, state: d.state,
      dueAt: d.run?.dueAt ?? scheduleOccurrences(r, date, date)[0]?.at ?? null,
      startMin: d.run?.startedAt ? d.metrics?.startMin ?? null : null, finishMin: d.metrics?.finishMin ?? null,
      durationMin: d.metrics?.durationMin ?? null, goalsMet: d.goalsMet, skipReason: d.run?.skipReason || null,
    });
  }
  return out.sort((a, b) => (a.dueAt ?? Infinity) - (b.dueAt ?? Infinity));
}
